import type { IDetailsPart, IFan, IHashboard, IMinerDetails } from '@/types/rc/miners'
import { IndividualPart, PartType } from '@/types/rc/miners/PartType'
import { fromPairs, get } from 'lodash'

const partTypesForKeys: Record<string, PartType> = {
  cb: PartType.ControlBoard,
  psu: PartType.PowerSupply,
  'hashboards.0': PartType.Hashboard,
  'hashboards.1': PartType.Hashboard,
  'hashboards.2': PartType.Hashboard,
  'hashboards.3': PartType.Hashboard,
  'fans.1': PartType.Fan,
  'fans.2': PartType.Fan,
  'fans.3': PartType.Fan,
  'fans.4': PartType.Fan,
}

export const partPaths: Record<IndividualPart, string | null> = {
  [IndividualPart.Miner]: null,
  [IndividualPart.CB]: 'cb',
  [IndividualPart.PSU]: 'psu',
  [IndividualPart.HB0]: 'hashboards.0',
  [IndividualPart.HB1]: 'hashboards.1',
  [IndividualPart.HB2]: 'hashboards.2',
  [IndividualPart.HB3]: 'hashboards.3',
  [IndividualPart.FAN1]: 'fans.1',
  [IndividualPart.FAN2]: 'fans.2',
  [IndividualPart.FAN3]: 'fans.3',
  [IndividualPart.FAN4]: 'fans.4',
}

/**
 * Stateless function to retrieve a part from the miner details
 * @param minerDetails IMinerDetails
 * @param partName IndividualPart
 * @returns ('cb') => ControlBoard, ('hb.0') => HB0, etc from IMinerDetails, etc...
 */
export const retrieveMinerPart = (minerDetails: IMinerDetails, partName: IndividualPart): IDetailsPart | null => {
  const key = partPaths[partName]
  if (!key) return null
  if (!key.includes('.')) {
    const part = get(minerDetails, key)
    return part ? { ...part, type: partTypesForKeys[key] } : null
  }
  if (key.includes('.')) {
    const [k, n] = key.split('.')
    const parts = get(minerDetails, k)
    const part = parts ? parts.find((e: IHashboard | IFan) => e.number == parseInt(n)) : null
    return part ? { ...part, type: partTypesForKeys[key] } : null
  }
  return null
}

/**
 * Stateless function to create a miner details with a part updated
 * @param minerDetails IMinerDetails
 * @param partType PartType
 * @param newPart IDetailsPart
 * @returns the updated miner details
 */
export const withPartUpdate = (
  minerDetails: IMinerDetails,
  partName: IndividualPart,
  update: (oldPart: IDetailsPart) => IDetailsPart
): IMinerDetails => {
  const key = partPaths[partName]

  // Retrieve the old part to update
  const oldPart = retrieveMinerPart(minerDetails, partName)
  if (!oldPart) throw new Error(`Part ${partName} not found in miner details`)

  if (key && !key.includes('.')) return { ...minerDetails, [key]: update(oldPart) }
  if (key && key.includes('.')) {
    const [k, n] = key.split('.')
    return {
      ...minerDetails,
      [k]: get(minerDetails, k).map((part: IHashboard | IFan) => (part.number == parseInt(n) ? update(oldPart) : part)),
    }
  }
  return minerDetails
}

/**
 * Stateless function to retrieve the dict of parts of a miner and zip the
 * part type along, ready to display on consolidation view
 * @param miner the miner to pull parts from
 * @param payload additional data to add to each part
 * @returns the list of IDetailsParts (or null if part is not available)
 */
export const toPartsDict = (miner: IMinerDetails, payload: Record<string, any> = {}) => {
  const parts = Object.values(IndividualPart).filter((ip) => ip !== IndividualPart.Miner)
  return fromPairs(parts.map((partKey) => [partKey, { ...retrieveMinerPart(miner, partKey), ...payload }]))
}

/**
 * Same as toPartsDict but returns an array of parts
 * @param miner
 * @returns
 */
export const toPartsArray = (miner: IMinerDetails, payload: Record<string, any> = {}) => {
  const parts = Object.values(IndividualPart).filter((ip) => ip !== IndividualPart.Miner)
  return parts.map((partKey) => ({ ...retrieveMinerPart(miner, partKey), key: partKey, ...payload }))
}
